import React, { useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Card, Chip, CircularProgress, Divider,
  Skeleton, Stack, Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import Bloco from './componentes/Bloco';
import Colunas from './componentes/Colunas';
import { listarPlanos, pedirAssinatura, teaser as buscarTeaser } from '../services/planoService';
import { rotuloDaCompetencia } from '../services/loteDoMes';
import {
  linhasDoComparativo, estadoDoTeaser, motivoDaVisita, frasesDoBloqueio
} from './planosApresentacao';
import TituloDaTela from './componentes/TituloDaTela';
import { LARGURA_DE_LEITURA } from './layout';

/**
 * Tela de Planos.
 *
 * Ela é visitada por quem NÃO paga — por definição. Por isso nada aqui aciona
 * modelo: o sinal exibido como amostra vem das 39 regras determinísticas, que
 * são código e custam zero. Gerar uma amostra por IA seria uma chamada por
 * visita a esta tela, feita justamente por quem não gerou receita.
 *
 * Três regras de honestidade que a tela cumpre à risca:
 *
 * 1. **Nada de borrão.** O sinal mais crítico aparece inteiro e legível. Texto
 *    embaçado só comunica "existe um parágrafo", e em ferramenta financeira lê
 *    como "estão escondendo um problema meu".
 * 2. **O que não existe é declarado.** A carteira multi-empresa do plano
 *    Consultor está em construção e a tela diz isso, em vez de listá-la como
 *    entrega. Descobrir depois de pagar custa mais caro que ler antes.
 * 3. **Ninguém é cobrado aqui.** Não há gateway no produto. O botão registra o
 *    pedido e o texto diz exatamente isso.
 */

const ICONE_DA_SEVERIDADE = {
  critical: <ErrorOutlineIcon fontSize="small" color="error" />,
  warning: <WarningAmberIcon fontSize="small" color="warning" />
};

const ROTULO_DA_SEVERIDADE = {
  critical: 'Crítico',
  warning: 'Atenção',
  positive: 'Positivo',
  info: 'Informativo'
};

// Quantos cartões o esqueleto desenha enquanto o catálogo não chega. O catálogo
// é a fonte da verdade, mas ele ainda não chegou — e o esqueleto tem de prometer
// o que a tela vai entregar, não o que ela entregava em outra versão.
// Quantas colunas o esqueleto desenha enquanto o catálogo não chegou. É uma
// aposta sobre o backend, e ela tem que bater: com uma coluna a menos, a tela
// pula de largura na hora em que o dado chega.
const PLANOS_ESPERADOS = [0, 1, 2];

const TelaPlanos = ({
  dadosParaTeaser = null,
  origem = null,
  bloqueio = null,
  autenticado = false,
  onIrPara,
  onEntrar
}) => {
  const [catalogo, setCatalogo] = useState(null);
  const [amostra, setAmostra] = useState(null);
  const [carregandoAmostra, setCarregandoAmostra] = useState(false);
  const [enviando, setEnviando] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);
  const [erro, setErro] = useState(null);
  // Estados de falha separados dos de carregamento: sem eles, requisição que
  // falha vira esqueleto para sempre — e a tela fica "carregando" num estado
  // que nunca vai chegar.
  const [catalogoFalhou, setCatalogoFalhou] = useState(false);
  const [amostraFalhou, setAmostraFalhou] = useState(false);

  useEffect(() => {
    let cancelado = false;
    listarPlanos()
      .then((d) => { if (!cancelado) setCatalogo(d); })
      .catch((e) => {
        if (cancelado) return;
        setCatalogoFalhou(true);
        setErro(`Não foi possível carregar os planos: ${e.message}`);
      });
    return () => { cancelado = true; };
  }, []);

  // O teaser roda sobre o dado que o navegador já tem. Se não houver dado, não
  // há amostra — e a tela diz isso, em vez de exibir um card vazio.
  //
  // Quem já assina NÃO recebe teaser: o convite fala de algo que essa pessoa já
  // tem, e vender de novo a quem já comprou é o jeito mais rápido de fazer o
  // produto parecer que não sabe quem está na frente dele.
  const ehGratuito = catalogo ? catalogo.atual === 'gratuito' : null;

  useEffect(() => {
    if (!dadosParaTeaser || ehGratuito !== true) return undefined;
    let cancelado = false;
    setCarregandoAmostra(true);
    setAmostraFalhou(false);
    buscarTeaser(dadosParaTeaser)
      .then((d) => { if (!cancelado) setAmostra(d); })
      .catch(() => {
        // Falha de rede NÃO pode virar "nenhum sinal disparou": isso afirmaria
        // que a empresa está bem quando o que houve foi a requisição não
        // chegar. Afirmar fato que não foi medido é o pior defeito deste
        // produto, e ele cabe até numa tela de marketing.
        if (cancelado) return;
        setAmostra(null);
        setAmostraFalhou(true);
      })
      .finally(() => { if (!cancelado) setCarregandoAmostra(false); });
    return () => { cancelado = true; };
  }, [dadosParaTeaser, ehGratuito]);

  const linhas = linhasDoComparativo(catalogo?.planos, catalogo?.atual, {
    autenticado,
    intencao: confirmacao?.intencao || catalogo?.intencao
  });
  const contexto = motivoDaVisita({
    origem,
    mesesBloqueados: bloqueio?.bloqueados || [],
    planoAtual: catalogo?.atual
  });
  const sinal = estadoDoTeaser(amostra, { temDados: Boolean(dadosParaTeaser) && ehGratuito === true });
  const avisoDeBloqueio = frasesDoBloqueio(bloqueio, rotuloDaCompetencia);

  const pedir = async (planoId) => {
    setEnviando(planoId);
    setErro(null);
    try {
      setConfirmacao(await pedirAssinatura(planoId));
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(null);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      {avisoDeBloqueio && (
        <Alert severity="warning" icon={<LockOutlinedIcon fontSize="small" />}>
          <AlertTitle>{avisoDeBloqueio.titulo}</AlertTitle>
          {avisoDeBloqueio.corpo} {avisoDeBloqueio.aindaPermitido}
        </Alert>
      )}

      {erro && <Alert severity="error" onClose={() => setErro(null)}>{erro}</Alert>}

      {confirmacao && (
        <Alert severity="success" onClose={() => setConfirmacao(null)}>
          <AlertTitle>Pedido registrado</AlertTitle>
          {confirmacao.mensagem}
        </Alert>
      )}

      {/* TRÊS "PLANOS" EMPILHADOS NA MESMA COLUNA — barra do topo, este <h1> e o
          título do primeiro bloco — é o tipo de repetição que faz uma tela
          parecer gerada. O <h1> fica, porque é a identidade da tela; quem ganha
          nome próprio é o bloco de baixo, que fala do sinal, não do preço. */}
      <TituloDaTela
        titulo="Planos"
        descricao="O que já é seu, o que a assinatura acrescenta, e quanto custa."
      />

      <Bloco
        titulo={contexto.titulo}
        pergunta={contexto.frase}
        acao={catalogo?.atual && catalogo.atual !== 'gratuito' ? (
          <Chip size="small" color="primary" variant="outlined" label="Assinatura ativa" />
        ) : null}
      >
        {/* Quem já assina não lê convite: lê o estado da assinatura dele. */}
        {ehGratuito === false && (
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '65ch' }}>
            Sua assinatura está ativa. Feche cada mês e a série cresce sem limite: quanto mais
            história, mais precisa fica a leitura. O Parecer do Consultor sai na tela Análise do
            Consultor, junto do plano de ação que você acompanha por lá.
          </Typography>
        )}

        {/* A amostra: o sinal mais crítico da PRÓPRIA empresa, inteiro. */}
        {(carregandoAmostra || (ehGratuito === null && !catalogoFalhou)) && (
          <Stack spacing={1}>
            <Skeleton variant="text" width="35%" height={20} />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="70%" />
          </Stack>
        )}

        {!carregandoAmostra && amostraFalhou && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '65ch' }}>
            Não foi possível calcular a amostra agora. Seus sinais continuam na tela Análise do
            Consultor, calculados sobre o dado que já está aqui.
          </Typography>
        )}

        {!carregandoAmostra && sinal.estado === 'comSinal' && (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              {ICONE_DA_SEVERIDADE[sinal.severidade]}
              <Typography variant="subtitle2" color="text.secondary">
                Seu sinal mais {sinal.severidade === 'critical' ? 'crítico' : 'relevante'} agora
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={ROTULO_DA_SEVERIDADE[sinal.severidade] || 'Sinal'}
              />
            </Stack>

            <Typography variant="h5" component="p" sx={{ maxWidth: '65ch', lineHeight: 1.4 }}>
              {sinal.fato}
            </Typography>

            {sinal.acao && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: '65ch' }}>
                {sinal.acao}
              </Typography>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ mt: 2 }}
            >
              {sinal.restantes > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {/* Contagem, não promessa: os outros sinais já estão na tela
                      de Análise, de graça. O que se compra é a leitura deles. */}
                  Outros {sinal.restantes} sinais foram calculados para a sua empresa.
                </Typography>
              )}
              {sinal.tela && onIrPara && (
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => onIrPara(sinal.tela)}
                  sx={{ ml: { sm: 'auto' }, minHeight: 44 }}
                >
                  Ver de onde vem
                </Button>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '75ch' }}>
              Este sinal é gratuito e sempre foi: ele diz O QUE está acontecendo. O Parecer do
              Consultor cruza os {sinal.restantes + 1} sinais entre si e diz POR QUE aconteceu e
              qual atacar primeiro.
            </Typography>
          </Box>
        )}

        {!carregandoAmostra && !amostraFalhou && sinal.estado === 'semSinal' && (
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '65ch' }}>
            Nenhum sinal disparou no período analisado — o que é uma boa notícia. O Parecer do
            Consultor é mais útil quando há um problema para priorizar.
          </Typography>
        )}

        {!carregandoAmostra && ehGratuito === true && sinal.estado === 'semDados' && (
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '65ch' }}>
            Lance um mês pela planilha ou pelo questionário e a análise inteira aparece — índice de
            saúde, sinais e plano de ação — sem assinatura.
          </Typography>
        )}
      </Bloco>

      {/* O comparativo. Nenhum plano vem pré-selecionado nem marcado como
          "recomendado": empurrar o caro é o comportamento que esta tela existe
          para não ter. */}
      {!catalogo && catalogoFalhou && (
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '65ch' }}>
            Os planos não carregaram. Recarregue a página — nada do que você lançou depende desta
            tela, e o plano gratuito continua ativo.
          </Typography>
        </Card>
      )}

      {/* O ESQUELETO PROMETIA TRÊS PLANOS E A TELA ENTREGAVA DOIS.
          Ele ficou em `[0, 1, 2]` de quando havia três, então o carregamento
          desenhava três cartões de 349px e o resultado eram dois de 536px: a
          página saltava de largura e de altura, e por um instante o produto
          dizia ter um plano a mais do que tem. Numa página de preço, isso é a
          pior hora possível para o usuário desconfiar do que está lendo. */}
      {!catalogo && !catalogoFalhou && (
        <Colunas colunas={PLANOS_ESPERADOS.length}>
          {PLANOS_ESPERADOS.map((i) => (
            <Skeleton variant="rounded" height={520} key={i} />
          ))}
        </Colunas>
      )}

      {catalogo && (
        <Colunas colunas={linhas.length}>
          {linhas.map((plano) => (
            <CartaoDePlano
              key={plano.id}
              plano={plano}
              enviando={enviando === plano.id}
              aoPedir={() => (plano.cta?.acao === 'entrar' ? onEntrar && onEntrar() : pedir(plano.id))}
            />
          ))}
        </Colunas>
      )}

      {/*
        O aviso fica, mas curto e sem expor a operação interna. Ele existe porque o
        botão diz "assinar" e não abre um checkout — sem uma linha dizendo que a
        confirmação chega por e-mail, o clique parece ter falhado. O que saiu foi
        a descrição de como a ativação é feita do nosso lado: isso é assunto
        nosso, não do cliente.
      */}
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, maxWidth: '75ch' }}>
        Você recebe a confirmação por e-mail para concluir a assinatura. A análise é calculada
        aqui mesmo, sem enviar seus números para nenhum provedor externo.
      </Typography>
    </Stack>
  );
};

/**
 * Um plano.
 *
 * O plano atual ganha borda de acento e o selo — e perde o botão. Cor só
 * significa saúde neste produto, então a distinção do plano vigente é feita por
 * borda e rótulo, nunca por fundo verde.
 */
const CartaoDePlano = ({ plano, enviando, aoPedir }) => (
  <Card
    variant="outlined"
    sx={{
      p: { xs: 2, sm: 3 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderColor: plano.atual ? 'primary.main' : 'divider',
      transition: 'border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)'
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
      <Typography variant="h4" component="h3">{plano.nome}</Typography>
      {plano.atual && <Chip size="small" color="primary" label="Seu plano" />}
    </Stack>

    {/* ALTURA MÍNIMA CALIBRADA PELA COPY MAIS LONGA, EM CADA FAIXA. Este texto
        fica ACIMA do preço, então cada linha a mais empurra o preço e a lista
        inteira daquele cartão para baixo — e três colunas de preço só são
        comparáveis a olho se as linhas se correspondem. Os 40px antigos foram
        medidos quando havia dois planos e a copy cabia em duas linhas; com
        três, medido em produção, o primeiro item de cada lista saía a 176px,
        196px e 178px do topo. */}
    {plano.para && (
      <Typography variant="body2" color="text.secondary" sx={{ minHeight: { md: 84, lg: 62 } }}>
        {plano.para}
      </Typography>
    )}

    {/* A FAIXA DO PREÇO TAMBÉM TEM ALTURA FIXA: dois cartões trazem um número de
        2,5rem e o terceiro traz a frase "Sob orçamento", menor. Sem a trava, o
        cartão do Full Time começa a lista acima dos outros dois. */}
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.75}
      // Entre 900px e 1199px a coluna mede ~240px e "por mês" cai para a
      // segunda linha ao lado de um número de 2,5rem — 64px de faixa contra
      // 46px dos outros dois cartões. Medido em produção: o Consultor começava
      // a lista 18px abaixo. Acima de 1200px a coluna volta a caber tudo numa
      // linha, e a faixa volta aos 46px.
      sx={{
        mt: 2,
        mb: 2,
        minHeight: { xs: '2.3rem', sm: '2.875rem', md: '4rem', lg: '2.875rem' }
      }}
    >
      {/* Mesmo tratamento do número protagonista dos blocos de leitura: 2,5rem,
          peso 600 e tracking negativo. O preço É o número protagonista deste
          cartão, e usar uma segunda escala aqui criaria um sistema paralelo. */}
      {/* SOB ORÇAMENTO NÃO É UM NÚMERO, E NÃO PODE USAR A ESCALA DE UM. A classe
          `valor-numerico` liga numeral tabular, que existe para alinhar
          dígitos e não faz nada por uma frase; e 2,5rem numa palavra de treze
          letras estoura a coluna do cartão no notebook. Aqui a frase entra na
          escala de título, que é o que ela é. */}
      <Typography
        component="p"
        className={plano.sobOrcamento ? undefined : 'valor-numerico'}
        sx={{
          fontSize: plano.sobOrcamento
            ? { xs: '1.375rem', sm: '1.5rem' }
            : { xs: '2rem', sm: '2.5rem' },
          fontWeight: 600,
          lineHeight: 1.15,
          letterSpacing: plano.sobOrcamento ? '-0.01em' : '-0.02em',
          // O cartão do meio tem preço alto e o da direita tem uma frase: sem
          // isto as três listas de entrega começam em alturas diferentes.
          alignSelf: 'baseline'
        }}
      >
        {plano.preco}
      </Typography>
      {plano.periodicidade && (
        <Typography variant="body2" color="text.secondary">{plano.periodicidade}</Typography>
      )}
    </Stack>

    <Stack spacing={1} sx={{ flex: 1 }}>
      {plano.entrega.map((linha) => (
        <Stack key={linha} direction="row" spacing={1} alignItems="flex-start">
          <CheckIcon fontSize="small" color="success" sx={{ mt: 0.25 }} />
          <Typography variant="body2">{linha}</Typography>
        </Stack>
      ))}
    </Stack>

    {plano.nota && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {plano.nota}
      </Typography>
    )}

    {plano.cta ? (
      <Button
        variant="contained"
        fullWidth
        disabled={plano.cta.desabilitado || enviando}
        onClick={aoPedir}
        sx={{ mt: 2, minHeight: 44 }}
        startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {/* O RÓTULO JÁ VEM DECIDIDO. Aqui existia um segundo lugar escolhendo a
            frase do botão ("Entrar para assinar"), e ele não sabia que um
            plano pode ser vendido sob orçamento — mandava assinar quem ia
            pedir um preço. Uma decisão, um lugar, e o teste alcança. */}
        {plano.cta.rotulo}
      </Button>
    ) : (
      /* O CARTÃO SEM BOTÃO ABRIA UM BURACO. Os dois cartões têm a mesma altura
         — como devem, numa tabela de preço —, e o plano em uso não tem ação a
         oferecer: sobrava um vão de mais de 150px no meio dele, e cartão com
         buraco lê como cartão que não carregou. Aqui o rodapé existe nos dois,
         na mesma linha de base: de um lado a ação, do outro o estado. */
      <Box
        sx={{
          mt: 2,
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'divider',
          color: 'text.secondary'
        }}
      >
        <CheckIcon fontSize="small" color="success" />
        <Typography variant="body2">
          {plano.atual ? 'Em uso agora — nada a fazer' : 'Sem ação necessária'}
        </Typography>
      </Box>
    )}
  </Card>
);

export default TelaPlanos;
